import React, { useMemo, useState } from 'react';
import { ComponentProps } from 'types';
import './index.scss';

interface TabItem {
  title: string;
  content: string;
}

interface TabsRuleData {
  defaultIndex?: number;
  tabs?: TabItem[];
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

export default function TabsRule({ data, commonStyle, style }: ComponentProps<TabsRuleData>) {
  const { tabs = [], defaultIndex = 0 } = data || {};

  // 过滤空 Tab，保证 BR-06 兜底逻辑：无内容时整体不展示
  const validTabs = useMemo(() => tabs.filter((tab) => tab && (tab.title || tab.content)), [tabs]);

  const initialIndex = validTabs.length ? clamp(defaultIndex, 0, validTabs.length - 1) : 0;
  const [activeIndex, setActiveIndex] = useState(initialIndex);

  if (!validTabs.length) return null;

  const safeActive = clamp(activeIndex, 0, validTabs.length - 1);
  const activeTab = validTabs[safeActive];

  const { tabColor, tabActiveColor, contentColor, fontSize, footerColor, footerFontSize } = style || {};

  const footerStyle: React.CSSProperties = {
    color: footerColor,
    fontSize: footerFontSize ? `${footerFontSize}px` : undefined,
  };

  const wrapperStyle: React.CSSProperties = {
    ...commonStyle,
    color: contentColor,
  };

  return (
    <div className="tabs-rule" style={wrapperStyle}>
      <div className="tabs-rule__head" role="tablist">
        {validTabs.map((tab, idx) => {
          const isActive = idx === safeActive;
          return (
            <div
              key={`${tab.title}-${idx}`}
              role="tab"
              aria-selected={isActive}
              className={`tabs-rule__tab ${isActive ? 'is-active' : ''}`}
              style={{
                color: isActive ? tabActiveColor : tabColor,
                fontSize: fontSize ? `${fontSize}px` : undefined,
              }}
              onClick={() => setActiveIndex(idx)}
            >
              <span className="tabs-rule__tab-text">{tab.title || `Tab ${idx + 1}`}</span>
              {isActive && <span className="tabs-rule__tab-bar" style={{ backgroundColor: tabActiveColor }} />}
            </div>
          );
        })}
      </div>
      {/* 富文本由 vision rich-text 控件产出，已自带 XSS 处理 */}
      {/* eslint-disable-next-line react/no-danger */}
      <div className="tabs-rule__content" dangerouslySetInnerHTML={{ __html: activeTab?.content || '' }} />
      <div className="tabs-rule__footer" style={footerStyle}>
        更多说明请滑动查看↓
      </div>
    </div>
  );
}
